import { FC, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Navigate, Outlet, useLocation } from 'react-router';

import { useAppDispatch, useAppSelector } from 'hooks/redux';
import * as routes from 'router/Routes';
import {
  authSelector,
  fetchMetaData,
} from 'store';
import { showErrorToast } from 'utils/toast';
import { setStartRoute } from 'store/layoutState/layoutState';

function getBaseRoute(path: string): string {
  if (path.startsWith(routes.DEVICES)) {
    return routes.DEVICES;
  }
  if (path.startsWith(routes.SOFTWARES)) {
    return routes.SOFTWARES;
  }
  if (path.startsWith(routes.ROLLOUTS)) {
    return routes.ROLLOUTS;
  }
  if (path.startsWith(routes.LWM2M)) {
    return routes.LWM2M;
  }
  if (path.startsWith(routes.HUSQIOTBUS)) {
    return routes.HUSQIOTBUS;
  }
  return routes.DEVICES;
}

// Auth route
export const AuthRoute: FC = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const {
    session: { isAuthenticated },
    authExpired,
  } = useAppSelector(authSelector);
  const { pathname } = useLocation();

  // Previous route
  const prevRoute = useMemo(() => {
    // Full path if auth expires
    if (authExpired) {
      return pathname;
    }
    return getBaseRoute(pathname);
  }, [pathname, authExpired]);

  useEffect(() => {
    return () => {
      // Set start route on unmount
      const startRoute = getBaseRoute(pathname);
      dispatch(setStartRoute(startRoute));
    };
  }, [dispatch, pathname]);

  // Fetch meta data
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchMetaData());
    }
  }, [dispatch, isAuthenticated]);

  // Show toasts when auth errors
  useEffect(() => {
    if (authExpired) {
      showErrorToast(intl.formatMessage({ id: 'misc.expired_session' }));
    }    
  }, [intl, authExpired]);

  // Redirect if unauthenticated
  if (!isAuthenticated) {
    localStorage.setItem('redirectTo', window.location.pathname);
    return <Navigate to={routes.LOGIN} state={{ prevRoute }} />;
  }

  return <Outlet />;
};
